import * as React from "react";
import { useState } from "react";
import TimeAgo from "timeago-react";
import { useGroupContext } from "groopit-js";
import { LikeContainer } from "../LikeContainer";
import {
  AssignmentDTO,
  ComponentType,
  GroopItFlag,
  GroopItModel_V1,
} from "groopit-js";
import { Comments } from "./Comments";
import { GroopitComponent } from "./GroopitComponent";
import { CopyGroopit, MailGroopit } from "./MailGroopit";
import { ErrorBoundary, FallbackProps } from "react-error-boundary";
import { GroopitTableComponent } from "./GroopitTableComponent";
import { Popper } from "@material-ui/core";
import { FollowUpComponentEditor } from "./FollowUpComponentEditor";
import { ExtractionSourceView } from "./ExtractionSourceView";
import { FlagIcon } from "../Icons/FlagIcon";

interface Props {
  groopit: GroopItModel_V1;
  assignment: AssignmentDTO;
  on_pinChange?: () => void;
  is_popover: boolean;
}

export const Groopit = (props: Props): JSX.Element => {
  const {
    groopit,
    assignment,
    on_pinChange,
    is_popover = false,
  } = { ...props };
  const [componentDatas, setComponentDatas] = useState(groopit.components);
  const [tagged, setTagged] = useState(groopit.tagged);
  const [flag, setFlag] = useState(groopit.flag);
  const [pinned, setPinned] = useState(groopit.pinned);
  const { flags, owner, user_id } = useGroupContext();

  // this open flag is a little hack to support the old bootstrap
  // styling of the nested context menu. Should really switch to
  // bootstrap-react but that will require a new stylesheet
  const [open, setOpen] = useState(false);
  const [submenuOpen, setSubmenuOpen] = useState(false);
  const [followUpEditorAnchor, setFollowUpEditorAnchor] =
    useState<HTMLElement | null>(null);

  const is_creator = groopit.creator.id == user_id;

  const set_flag = async (flag: string) => {
    const m = await import("groopit-js");

    const client = new m.GroopItApiClient({});

    await client.flagGroopIt(
      groopit.id,
      GroopItFlag.fromJS({
        Flag: flag,
      }),
    );
    setFlag(flag);
    setOpen(false);
    setSubmenuOpen(false);
  };

  const toggle_pin = async () => {
    const m = await import("groopit-js");

    const client = new m.GroopItApiClient({});
    if (pinned) {
      await client.unPin(groopit.id);
    } else {
      await client.pin(groopit.id);
    }
    groopit.pinned = !pinned;
    setPinned(!pinned);
    setOpen(false);
    on_pinChange?.();
  };

  const isLinkedToSalesforce = (groopit: GroopItModel_V1) => {
    // We don't show Salesforce UI if there's no Salesforce data
    return (
      groopit.salesforceLinks.length > 0 ||
      groopit.components.filter(
        (c) => c.model.type === ComponentType.SaveToSalesforce,
      ).length > 0
    );
  };

  return (
    <div className="groopit">
      <div className="overflow-visible">
        <div>
          <div className="header">
            <span className="text-display-title text-color-secondary flex-grow flex-shrink min-w-0 self-center">
              {assignment.title}
              {groopit.pinned && (
                <img
                  title="Pinned"
                  src="/Content/Images/pinned.png"
                  className="mt-4 ml-2 h-4"
                />
              )}
            </span>
            <div className="flex flex-col flex-grow flex-shrink min-w-0 self-center">
              <span className="text-display-subtitle text-color-secondary groopit-creator text-right truncate">
                {groopit.creator.friendlyName}
              </span>
              <span className="text-display-caption text-color-hint text-right mt-1 whitespace-no-wrap">
                <TimeAgo datetime={groopit.timestamp} />
              </span>
              {groopit.source && (
                <span className="text-display-caption text-color-hint text-right mt-1 whitespace-no-wrap">
                  {groopit.source}
                </span>
              )}
            </div>
            <div className="flex items-center self-center min-w-fit-content">
              <img
                title="Avatar"
                src={groopit.creator.avatarUrl}
                className="img-circle avatar-img pull-right my-2 ml-4"
              />
              <div
                onBlur={(e) => {
                  if (!e.currentTarget.contains(e.relatedTarget as Node)) {
                    // Not triggered when swapping focus between children
                    setOpen(false);
                  }
                }}
                className={`dropdown text-center w-6 ${open ? "open" : ""}`}
              >
                <button
                  title="menu"
                  className="btn-none"
                  type="button"
                  onClick={() => {
                    setOpen(!open);
                  }}
                >
                  <span className="glyphicon glyphicon-option-vertical text-color-hint"></span>
                </button>
                <ul className="dropdown-menu pull-right">
                  {owner && (
                    <li className="dropdown-submenu">
                      <a
                        className="dropdown-submenu-target"
                        tabIndex={-1}
                        onClick={() => {
                          setSubmenuOpen(!submenuOpen);
                        }}
                      >
                        <FlagIcon className="menu-icon" />
                        Flag<span className="caret"></span>
                      </a>
                      <ul
                        className={`dropdown-menu top-4 left-0 ${
                          submenuOpen ? "display-block" : "display-none"
                        }`}
                      >
                        {flags && flags.length > 0 && (
                          <>
                            {flags.map((f, i) => (
                              <li key={i}>
                                <a
                                  href="#"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    set_flag(f);
                                  }}
                                  className="cursor-pointer"
                                >
                                  <FlagIcon className="menu-icon" />
                                  {f}
                                </a>
                              </li>
                            ))}
                            <li className="divider my-0" />
                          </>
                        )}
                        <li>
                          <a
                            href="#"
                            className="cursor-pointer"
                            onClick={(e) => {
                              e.preventDefault();
                              set_flag("");
                            }}
                          >
                            <img
                              title="Clear flag"
                              className="menu-icon"
                              src="/Content/Images/circle-x.png"
                            />
                            Clear flag
                          </a>
                        </li>
                        <li>
                          <a
                            href={`/Groop/ManageGroopItFlags/${groopit.parentGroop.id}`}
                          >
                            <span>
                              <img
                                title="Manage Flags"
                                className="menu-icon"
                                src="/Content/Images/manage.png"
                              />
                              Manage flags
                            </span>
                          </a>
                        </li>
                      </ul>
                    </li>
                  )}
                  {owner && (
                    <li onClick={() => toggle_pin()}>
                      <a href="#">
                        <span>
                          <img
                            title="Pin Data"
                            className="menu-icon"
                            src="/Content/Images/pinned.png"
                          />
                          {groopit.pinned ? "Unpin data" : "Pin data"}
                        </span>
                      </a>
                    </li>
                  )}
                  {(owner || is_creator) && (
                    <>
                      <li>
                        <a href={`/Groopit/Edit/${groopit.id}`}>
                          <span>
                            <img
                              title="Edit data"
                              className="menu-icon"
                              src="/Content/Images/edit.png"
                            />
                            Edit
                          </span>
                        </a>
                      </li>
                      <li>
                        <a href={`/Groopit/Delete/${groopit.id}`}>
                          <span>
                            <img
                              title="Delete"
                              className="menu-icon"
                              src="/Content/Images/delete.png"
                            />
                            Delete
                          </span>
                        </a>
                      </li>
                    </>
                  )}
                  <li>
                    <MailGroopit {...props}>
                      <span>
                        <img
                          title="Email"
                          className="menu-icon"
                          src="/Content/Images/mail.png"
                        />
                        Send
                      </span>
                    </MailGroopit>
                  </li>
                  <li>
                    <CopyGroopit {...props}>
                      <span>
                        <img
                          title="Copy"
                          className="menu-icon"
                          src="/Content/Images/duplicate.png"
                        />
                        Copy
                      </span>
                    </CopyGroopit>
                  </li>
                  <li>
                    {/* eslint-disable-next-line react/jsx-no-target-blank */}
                    <a
                      href={`/Groopit/Details/${groopit.id}?Print=true`}
                      target="_blank"
                    >
                      <span>
                        <img
                          title="Print"
                          className="menu-icon"
                          src="/Content/Images/printer.png"
                        />
                        Print
                      </span>
                    </a>
                  </li>
                </ul>
              </div>
            </div>
          </div>
          <hr className="small-divider delete-margins mt-2 mb-2 border-app-gray-06" />
        </div>
        {groopit.editTime &&
          groopit.editor &&
          groopit.editor.id != groopit.creator.id && (
            <div className="text-right">
              <span className="text-display-caption text-color-hint">
                Edited by {groopit.editor.friendlyName}{" "}
                <TimeAgo datetime={groopit.editTime} />
              </span>
            </div>
          )}
      </div>
      <div className="groopit-flag">{flag}</div>
      <div className="groopit-component-container">
        {assignment.components.map((model) =>
          model.type === ComponentType.Table ? (
            <GroopitTableComponent
              key={model.componentModel_Id}
              {...props}
              model={model}
              childComponents={componentDatas.filter(
                (c) =>
                  c.model.parentModelComponentModel_Id ===
                  model.componentModel_Id,
              )}
              allComponents={componentDatas}
              is_creator={is_creator}
              groopit_id={groopit.id}
              isLinkedToSalesforce={isLinkedToSalesforce(groopit)}
              openFollowUpEditor={setFollowUpEditorAnchor}
            />
          ) : model.parentModelComponentModel_Id != null &&
            assignment.components.find(
              (c) => c.componentModel_Id == model.parentModelComponentModel_Id,
            )?.type === ComponentType.Table ? null : (
            <GroopitComponent
              key={model.componentModel_Id}
              model={model}
              assignment={assignment}
              component={componentDatas.find(
                (c) => c.model.componentModel_Id == model.componentModel_Id,
              )}
              allComponents={componentDatas}
              is_popover={is_popover}
              groopit_id={groopit.id}
              isLinkedToSalesforce={isLinkedToSalesforce(groopit)}
              openFollowUpEditor={(e) =>
                is_popover
                  ? (window.location.href = `/Groopit/Details/${groopit.id}`)
                  : setFollowUpEditorAnchor(e)
              }
            />
          ),
        )}
      </div>
      {groopit.salesforceLinks?.length > 0 && (
        <div className="groopit-component">
          <div className="text-display-subtitle app-gray-03 pt-4 pb-2">
            Added to Salesforce:{" "}
            {groopit.salesforceLinks.map((sl, index, arr) => (
              <span key={sl.fieldName}>
                <a
                  className="external-service-link"
                  href={`/Groopit/Salesforce/${groopit.id}?objectId=${sl.selectedObjectId}`}
                >
                  {sl.name}
                </a>
                {`${index < arr.length - 1 ? ", " : ""}`}
              </span>
            ))}
          </div>
        </div>
      )}
      {!is_popover && (
        <ExtractionSourceView extractionSource={groopit.extractionSource} />
      )}
      {!is_popover &&
        followUpEditorAnchor &&
        assignment.components
          .filter((c) => c.type == ComponentType.FollowUpRequest)
          .map((request) => (
            <Popper
              key={`followup-editor-${request.componentModel_Id}`}
              open={Boolean(followUpEditorAnchor)}
              anchorEl={followUpEditorAnchor}
              placement="bottom-start"
              modifiers={{
                flip: {
                  enabled: false,
                },
              }}
            >
              <div className="modal-content w-96 md:w-112 lg:w-80">
                <FollowUpComponentEditor
                  groupId={groopit.parentGroop.id}
                  groopItId={groopit.id}
                  componentModels={assignment.components.filter(
                    (c) => c.isFollowUpField,
                  )}
                  componentDatas={componentDatas.filter(
                    (c) => c.model.isFollowUpField,
                  )}
                  tags={tagged ?? {}}
                  isLinkedToSalesforce={isLinkedToSalesforce(groopit)}
                  onGroopitUpdated={(groopit) => {
                    setComponentDatas(groopit.components);
                    setTagged(groopit.tagged);
                  }}
                  onClose={() => setFollowUpEditorAnchor(null)}
                />
              </div>
            </Popper>
          ))}
      {!is_popover && (
        <>
          <hr className="small-divider delete-margins" />
          <div className="like-container">
            <LikeContainer
              groopit={groopit.id}
              likedByUser={groopit.likedByUser ? "True" : "False"}
              likesCount={groopit.likeCount}
              canComment={groopit.canComment}
            />
          </div>
          <Comments
            groopit={groopit}
            group_id={groopit.parentGroop.id}
            can_manage={owner}
          />
        </>
      )}
    </div>
  );
};

const GroopitError = (props: FallbackProps): JSX.Element => {
  const { error } = props;
  return (
    <div role="alert" className="groopit">
      <p>Something went wrong displaying this post</p>
      <p>{error.message}</p>
    </div>
  );
};

export const GroopitWithErrorBoundary = (props: Props): JSX.Element => {
  return (
    <ErrorBoundary FallbackComponent={GroopitError}>
      <Groopit {...props} />
    </ErrorBoundary>
  );
};
